<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <BoxWithTitle
      openingIcon="mdi-chevron-down"
      closingIcon="mdi-chevron-up"
    >
      <template #header>
        <v-toolbar-title>
          {{ $t('members.memberInfo') }}
        </v-toolbar-title>
      </template>

      <template #content>
        <form>
          <MemberForm
            ref="mainForm"
            :member="updatedData"
            :roles="roles"
            :dirty="dirty"
            :fromSection="fromSection"
            @dirtyChanged="isFormDirty = $event"
            @validChanged="isFormValid = $event"
            @sendData="updateData"
          />
          <div
            v-if="showSaveBtn"
            :class="`btnSaveStyle ${isSuperAdmin ? 'styleIfSuperAdmin' : 'styleIfNotSuperAdmin'}`"
          >
            <VCheckbox
              v-if="isSuperAdmin"
              v-model="updatedData.excludeInBilling"
              :label="$t('forms.labels.excludeInBilling')"
              @input="updateExcludeFromBilling"
            />
            <div>
              <v-btn
                text
                :disabled="buttonDisable"
                @click="onClickConfirmRemove"
              >
                <span
                  :class="buttonDisable ? '' : 'text-danger'"
                >
                  {{ leaveTeam }}
                </span>
              </v-btn>
              <v-btn
                :disabled="buttonDisable"
                type="submit"
                color="secondary"
                @click.prevent="onClickUpdate"
              >
                {{ $t('forms.btn.save') }}
              </v-btn>
            </div>
          </div>
        </form>
      </template>
    </BoxWithTitle>
    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirtyChanged', dirty);"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('validChanged', valid)"
    />
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import MemberForm from '@/components/team/MemberForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle.vue';
import FormPageMixin from '@/mixins/FormPageMixin';
import roles from '@/constants/roles';
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';

export default {
    components: {
        VCheckbox,
        MemberForm,
        BoxWithTitle,
        ValidationObserver,
        SlotScopeWatcher,
    },
    mixins: [
        FormPageMixin,
    ],
    props: {
        fromSection: {
            type: String,
            default: '',
        },
        updateRole: {
            type: Function,
            default: () => null,
        },
    },
    data() {
        return {
            isMemberMyself: null,
            isSuperAdmin: null,
        };
    },
    computed: {
        ...mapGetters([
            'defaultUserRoute',
            'team',
            'member',
            'hasPermission',
            'hasRole',
            'teamID',
            'superAdmin',
        ]),
        canLabEdit() {
            return !(this.hasRole('laboratoryManager') && this.labRoles.includes(this.member.roles[0].key) && this.hasPermission('members.roles.edit'));
        },
        showSaveBtn() {
            return this.isAdmin || this.hasRole(roles.MANAGER.value) || this.hasRole(roles.LABORATORY_MANAGER.value);
        },
        defaultData() {
            return this.member;
        },
        isAdmin() {
            return this.hasRole(roles.ADMINISTRATOR.value);
        },
        labRoles() {
            return [
                roles.LABORATORY_MANAGER.value,
                roles.LABORATORY_MICROBIOLOGIST.value,
                roles.LABORATORY_CHEMIST.value,
                roles.LABORATORY_TECHNICIAN.value,
            ];
        },
        roles() {
            // For operators
            if (this.fromSection === 'clientTeam') {
                return null;
            }
            const { FINANCIAL_ADMINISTRATOR, ADMINISTRATOR, ...rolesRest } = roles;
            let initRoles = rolesRest;
            if (this.superAdmin) {
                initRoles = {
                    ...initRoles,
                    FINANCIAL_ADMINISTRATOR: roles.FINANCIAL_ADMINISTRATOR,
                };
            }
            if (this.isAdmin) {
                initRoles = {
                    ADMINISTRATOR: roles.ADMINISTRATOR,
                    ...initRoles,
                };
            }
            if (this.hasRole('laboratoryManager')) {
                initRoles = Object.values(roles)
                    .filter(role => this.labRoles.includes(role.value));
            }
            return Object.values(initRoles).map(role => ({
                label: this.$t(`roles.${role.value}`),
                value: role.value,
                overrides: role.overrides,
            }));
        },
        leaveTeam() {
            return this.isMemberMyself ? this.$t('teams.leave') : this.$t('members.removeFromTeam');
        },
        buttonDisable() {
            return this.canLabEdit && !(this.isAdmin || this.hasRole(roles.MANAGER.value));
        },
    },
    async created() {
        await this.getSelfInfo();
        this.$refs.observer.reset();
    },
    methods: {
        ...mapActions({
            reloadMember: 'getMemberInfo',
            resetMember: 'resetMemberInfo',
            refreshUserTeams: 'refreshUserTeams',
            resetAccessControlState: 'resetAccessControlState',
            disablePreventQuitFormular: 'disablePreventQuitFormular',
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),
        onClickConfirmRemove() {
            this.$root.$emit('showModal', {
                title: this.$t('members.removeMember'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('members.confirmRemove') },
                onConfirm: () => this.removeRequest(),
                isForm: false,
            });
        },
        async removeRequest() {
            const { id, firstName, lastName } = this.member;
            const { clientId } = this.$route.params;
            if (this.fromSection === 'clientTeam') {
                await this.$rq.deleteClientMember(clientId, id);
            } else {
                await this.$rq.removeMemberFromTeam(id);
            }

            this.resetMember();

            this.$toasted.global.toast_success({
                message: this.$t('msg.memberRemoved', {
                    data: `${firstName} ${lastName}`,
                }),
            });

            if (this.isMemberMyself) {
                await this.refreshUserTeams();
                await this.resetAccessControlState();
                await this.$router.push({ name: this.defaultUserRoute });
            } else {
                this.$router.go(-1);
            }
        },
        async getSelfInfo() {
            const { data } = await this.$rq.getMemberInfo('me');
            this.isSuperAdmin = data?.superAdmin;
            this.isMemberMyself = this.member.id === data.id;
        },
        async updateExcludeFromBilling() {
            await this.onClickUpdate();
            this.$toasted.global.toast_success({
                message: this.$t('msg.excludeInBilling'),
            });
        },
        preparePayload() {
            if (!isEmpty(this.updatedData.phoneNumber) && !this.updatedData.phoneNumber.valid) {
                this.$toasted.global.toast_error({
                    message: this.$t('profile.phoneError'),
                });
            }
            const payload = {
                profile: {
                    firstName: this.updatedData.firstName,
                    lastName: this.updatedData.lastName,
                    phoneNumber: this.updatedData.phoneNumber,
                    excludeInBilling: this.updatedData.excludeInBilling,
                },
                preferences: {
                    locale: this.updatedData.lang,
                },
            };
            if (this.member.email !== this.updatedData.email) {
                payload.local = {};
                payload.local.email = this.updatedData.email;
                payload.local.verified = false;
            }
            return payload;
        },
        async onClickUpdate() {
            const isEmailChanged = this.member.email !== this.updatedData.email;
            const { id, firstName, lastName } = this.updatedData;
            if (this.member.firstName !== this.updatedData.firstName
              || this.member.lastName !== this.updatedData.lastName
              || this.phoneNumber !== this.updatedData.phoneNumber
              || isEmailChanged) {
                await this.$rq.updateProfile(this.member.user, this.preparePayload());
            }
            // If member's role has been changed
            if (this.member.roles[0].key !== this.updatedData.roles[0].key) {
                await this.$rq.updateMemberRole(id, this.updatedData);

                // Reset access groups:
                await this.$rq.updateMemberAccessGroup(id, { accessGroups: [] });
                /* For Update the second component member Access */
                this.updateRole();
                this.getSelfInfo();
            }
            await this.reloadMember(id);

            this.cancelModifications();
            this.$toasted.global.toast_success({
                message: this.$t('msg.memberModified', {
                    data: `${firstName} ${lastName}`,
                }),
            });
            this.disablePreventQuitFormular();
        },
    },
};
</script>

<style lang="scss" scoped>
.btnSaveStyle{
    display: flex;
    align-items: center;
}
.styleIfSuperAdmin{
    justify-content: space-between;
}
.styleIfNotSuperAdmin{
    justify-content: end;
}
</style>
